import { useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  FileText,
  Download,
  FileSpreadsheet,
  Server,
  ShieldAlert,
  AlertTriangle,
  Shield,
  BarChart3,
  Loader2,
  CheckCircle
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const reportTypes = [
  {
    id: 'compliance',
    title: 'Relatório de Conformidade NIS2',
    description: 'Estado de conformidade com todos os requisitos da Diretiva NIS2 e Lei 125/2025',
    icon: Shield,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  {
    id: 'assets',
    title: 'Inventário de Ativos',
    description: 'Lista completa de ativos de informação com classificação de segurança',
    icon: Server,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  {
    id: 'risks',
    title: 'Análise de Riscos',
    description: 'Riscos identificados, níveis de risco e medidas de mitigação',
    icon: ShieldAlert,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
  {
    id: 'incidents',
    title: 'Relatório de Incidentes',
    description: 'Histórico de incidentes de cibersegurança e seu estado de resolução',
    icon: AlertTriangle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
  },
  {
    id: 'vulnerabilities',
    title: 'Relatório de Vulnerabilidades',
    description: 'Vulnerabilidades identificadas nos ativos e estado de remediação',
    icon: BarChart3,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
];

export default function Reports() {
  const [isGenerating, setIsGenerating] = useState({});
  const [previewData, setPreviewData] = useState(null);

  const generateReport = async (reportType, format) => {
    setIsGenerating(prev => ({ ...prev, [reportType]: format }));
    
    try {
      const response = await api.get(`/reports/${reportType}`);
      const data = response.data;
      
      if (format === 'preview') {
        setPreviewData({ type: reportType, data });
        toast.success('Pré-visualização gerada');
      } else if (format === 'excel') {
        exportToExcel(reportType, data);
        toast.success('Relatório Excel exportado');
      } else if (format === 'pdf') {
        exportToPDF(reportType, data);
        toast.success('Relatório PDF gerado');
      }
    } catch (error) {
      toast.error('Erro ao gerar relatório');
    } finally {
      setIsGenerating(prev => ({ ...prev, [reportType]: null }));
    }
  };

  const exportToExcel = (reportType, data) => {
    const wb = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ['NIS2AI - Relatório'],
      ['Tipo:', reportTypes.find(r => r.id === reportType)?.title],
      ['Data:', new Date().toLocaleDateString('pt-PT')],
      [''],
    ];
    
    if (data.summary) {
      summaryData.push(['Resumo']);
      Object.entries(data.summary).forEach(([key, value]) => {
        summaryData.push([key.replace(/_/g, ' '), value]);
      });
    }
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Resumo');
    
    // Data sheet
    let dataSheet;
    if (reportType === 'compliance' && data.requirements) {
      dataSheet = XLSX.utils.json_to_sheet(data.requirements.map(r => ({
        'Código': r.code,
        'Título': r.title,
        'Categoria': r.category,
        'Criticidade': r.criticality,
        'Estado': r.compliance_status,
        'Base Legal': r.legal_basis,
      })));
      XLSX.utils.book_append_sheet(wb, dataSheet, 'Requisitos');
    } else if (reportType === 'assets' && data.assets) {
      dataSheet = XLSX.utils.json_to_sheet(data.assets.map(a => ({
        'ID': a.asset_id,
        'Nome': a.name,
        'Tipo': a.asset_type,
        'Criticidade': a.criticality,
        'Departamento': a.department,
        'Estado': a.operational_status,
        'Localização': a.physical_location,
      })));
      XLSX.utils.book_append_sheet(wb, dataSheet, 'Ativos');
    } else if (reportType === 'risks' && data.risks) {
      dataSheet = XLSX.utils.json_to_sheet(data.risks.map(r => ({
        'ID': r.risk_id,
        'Título': r.title,
        'Categoria': r.category,
        'Probabilidade': r.probability,
        'Impacto': r.impact,
        'Nível': r.risk_level,
        'Estado': r.status,
      })));
      XLSX.utils.book_append_sheet(wb, dataSheet, 'Riscos');
    } else if (reportType === 'incidents' && data.incidents) {
      dataSheet = XLSX.utils.json_to_sheet(data.incidents.map(i => ({
        'ID': i.incident_id,
        'Título': i.title,
        'Tipo': i.incident_type,
        'Severidade': i.severity,
        'Estado': i.status,
        'Data Criação': i.created_at,
      })));
      XLSX.utils.book_append_sheet(wb, dataSheet, 'Incidentes');
    } else if (reportType === 'vulnerabilities' && data.vulnerabilities) {
      dataSheet = XLSX.utils.json_to_sheet(data.vulnerabilities.map(v => ({
        'ID': v.vulnerability_id,
        'CVE': v.cve_id,
        'Título': v.title,
        'Severidade': v.severity,
        'CVSS': v.cvss_score,
        'Estado': v.status,
        'Ativo': v.asset_id,
      })));
      XLSX.utils.book_append_sheet(wb, dataSheet, 'Vulnerabilidades');
    }
    
    XLSX.writeFile(wb, `nis2ai_${reportType}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToPDF = async (reportType, data) => {
    // Use backend PDF generation for better quality
    try {
      const response = await api.get(`/pdf-reports/${reportType}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nis2ai_${reportType}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Fallback to print-based PDF
      const reportInfo = reportTypes.find(r => r.id === reportType);
      
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>NIS2AI - ${reportInfo?.title}</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; margin: 40px; color: #1e293b; }
            h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
            h2 { color: #334155; margin-top: 30px; }
            .summary { background: #f1f5f9; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .summary-item { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e2e8f0; }
            .summary-label { font-weight: 600; }
            .summary-value { font-size: 1.2em; color: #1e40af; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th { background: #1e293b; color: white; padding: 12px; text-align: left; }
            td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
            tr:nth-child(even) { background: #f8fafc; }
            .footer { margin-top: 40px; text-align: center; color: #64748b; font-size: 12px; }
            @media print { body { margin: 20px; } }
          </style>
        </head>
        <body>
          <h1>NIS2AI - ${reportInfo?.title}</h1>
          <p>Gerado em: ${new Date().toLocaleDateString('pt-PT')} às ${new Date().toLocaleTimeString('pt-PT')}</p>
      `;
      
      if (data.summary) {
        htmlContent += '<div class="summary"><h2>Resumo</h2>';
        Object.entries(data.summary).forEach(([key, value]) => {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          htmlContent += `<div class="summary-item"><span class="summary-label">${label}</span><span class="summary-value">${value}</span></div>`;
        });
        htmlContent += '</div>';
      }
      
      htmlContent += '<div class="footer">NIS2AI - Plataforma de Conformidade NIS2 &amp; EU AI Act</div></body></html>';
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <Layout title="Relatórios">
      <div className="space-y-6" data-testid="reports-container">
        {/* Reports Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reportTypes.map((report) => {
            const Icon = report.icon;
            const isLoading = isGenerating[report.id];
            
            return (
              <Card key={report.id} className="card-flat" data-testid={`report-card-${report.id}`}>
                <CardHeader>
                  <div className={`h-12 w-12 rounded-lg ${report.bgColor} flex items-center justify-center mb-3`}>
                    <Icon className={`h-6 w-6 ${report.color}`} />
                  </div>
                  <CardTitle className="text-lg">{report.title}</CardTitle>
                  <CardDescription>{report.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateReport(report.id, 'preview')}
                      disabled={!!isLoading}
                      data-testid={`preview-${report.id}`}
                    >
                      {isLoading === 'preview' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Pré-visualizar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateReport(report.id, 'excel')}
                      disabled={!!isLoading}
                      data-testid={`excel-${report.id}`}
                    >
                      {isLoading === 'excel' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Excel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => generateReport(report.id, 'pdf')}
                      disabled={!!isLoading}
                      data-testid={`pdf-${report.id}`}
                    >
                      {isLoading === 'pdf' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      <FileText className="mr-2 h-4 w-4" />
                      PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Preview Section */}
        {previewData && (
          <Card className="card-flat">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Pré-visualização: {reportTypes.find(r => r.id === previewData.type)?.title}</span>
                <Button variant="ghost" size="sm" onClick={() => setPreviewData(null)}>
                  Fechar
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              {previewData.data.summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {Object.entries(previewData.data.summary).map(([key, value]) => (
                    <div key={key} className="bg-slate-50 rounded-lg p-4">
                      <p className="text-2xl font-bold text-slate-900">{value}</p>
                      <p className="text-sm text-slate-500 capitalize">
                        {key.replace(/_/g, ' ')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Compliance specific */}
              {previewData.type === 'compliance' && previewData.data.by_category && (
                <div className="space-y-4">
                  <h3 className="font-medium text-slate-900">Conformidade por Categoria</h3>
                  {Object.entries(previewData.data.by_category).map(([cat, info]) => (
                    <div key={cat} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 capitalize">{cat.replace(/_/g, ' ')}</span>
                        <span className="text-sm font-medium">{info.conforme}/{info.total} ({info.percentage}%)</span>
                      </div>
                      <Progress value={info.percentage} className="h-2" />
                    </div>
                  ))}
                </div>
              )}

              {/* Assets specific */}
              {previewData.type === 'assets' && previewData.data.by_criticality && (
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-slate-900 mb-4">Por Criticidade</h3>
                    {Object.entries(previewData.data.by_criticality).map(([crit, count]) => (
                      <div key={crit} className="flex items-center justify-between py-2 border-b border-slate-100">
                        <Badge variant="outline" className={`badge-${crit}`}>{crit}</Badge>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-4">Por Tipo</h3>
                    {Object.entries(previewData.data.by_type).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-600 capitalize">{type}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risks specific */}
              {previewData.type === 'risks' && previewData.data.by_level && (
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-slate-900 mb-4">Por Nível de Risco</h3>
                    {Object.entries(previewData.data.by_level).map(([level, count]) => (
                      <div key={level} className="flex items-center justify-between py-2 border-b border-slate-100">
                        <Badge variant="outline" className={`badge-${level}`}>{level}</Badge>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-4">Por Categoria</h3>
                    {Object.entries(previewData.data.by_category).map(([cat, count]) => (
                      <div key={cat} className="flex items-center justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-600 capitalize">{cat.replace(/_/g, ' ')}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
